/**
 * GraphQL Expense Service
 * Connects to Amplify Mock or real AppSync API for expense operations
 */

import { generateClient } from 'aws-amplify/api';
import {
  Expense,
  ExpenseStatus,
  CreateExpenseInput,
  UpdateExpenseInput,
  ExpenseConnection,
} from '../types';
import { canonicalizeEmail, identityVariants, sameIdentity } from './identity';
import { StorageService } from './storage';

// Lazy initialize client to avoid "Amplify not configured" error
let client: any = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// Helper to get auth mode for queries
// Use userPool auth since AppSync default is Cognito User Pool
const getAuthMode = (): { authMode: 'apiKey' | 'userPool' } => {
  // Use userPool auth - user must be logged in
  return { authMode: 'userPool' as const };
};

// Helper to extract data from GraphQL result
const getData = (result: any): any => {
  if ('data' in result) {
    return result.data;
  }
  return null;
};

/**
 * Read a connection page ({ items, nextToken }) from a resolved GraphQL result
 * OR from a thrown Amplify error that still carries a partial payload.
 *
 * AppSync answers with HTTP 200 and `items: [row, null, row, ...]` whenever a
 * stored row violates a field the schema marks non-null — an expense saved
 * without paymentMethod, for instance. Amplify v6 reports that as a throw with
 * the partial data hanging off `error.data`, so both shapes must be read the
 * same way or the rows that did resolve are discarded along with the ones that
 * didn't.
 */
const extractPage = (resultOrError: any, field: string): { items: any[]; nextToken?: string } => {
  const page = resultOrError?.data?.[field];
  return {
    items: Array.isArray(page?.items) ? page.items : [],
    nextToken: page?.nextToken || undefined,
  };
};

/**
 * Keep only the rows AppSync actually resolved. A single malformed row must
 * never take the rest of the list down with it.
 */
const liveRows = (rawItems: any[], context: string): any[] => {
  const usable = rawItems.filter(item => item && item.id);
  const nulled = rawItems.length - usable.length;
  if (nulled > 0) {
    console.warn(
      `[GraphQL] ${context}: ignored ${nulled} row(s) returned as null by AppSync ` +
      `— the stored item is missing a field the schema marks non-null`
    );
  }
  return usable;
};

/**
 * Walk every page of a connection query and return the rows that resolved.
 *
 * A page that fails outright stops the walk, but its partial payload is still
 * harvested: losing one bad page should not mean losing the pages already read.
 */
const collectPages = async (
  query: string,
  field: string,
  variables: Record<string, any>,
  context: string
): Promise<any[]> => {
  const collected: any[] = [];
  let nextToken: string | undefined = undefined;

  do {
    let page: { items: any[]; nextToken?: string };
    try {
      const result: any = await getClient().graphql({
        query,
        variables: { ...variables, nextToken },
        ...getAuthMode(),
      } as any);
      if (result?.errors?.length) {
        console.warn(`[GraphQL] ${context}: partial errors`, JSON.stringify(result.errors));
      }
      page = extractPage(result, field);
    } catch (pageError: any) {
      page = extractPage(pageError, field);
      const recovered = liveRows(page.items, context);
      console.warn(
        `[GraphQL] ${context}: page failed (${pageError?.message || pageError}); ` +
        `recovered ${recovered.length} row(s)`
      );
      collected.push(...recovered);
      break;
    }
    collected.push(...liveRows(page.items, context));
    nextToken = page.nextToken;
  } while (nextToken);

  return collected;
};

// GraphQL Queries - Updated to match AppSync schema
const listExpensesQuery = /* GraphQL */ `
  query ListExpenses($limit: Int, $nextToken: String) {
    listExpenses(limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        category
        amount
        date
        description
        paymentMethod
        status
        submitterId
        department
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const getAllExpensesQuery = /* GraphQL */ `
  query GetAllExpenses($limit: Int, $nextToken: String) {
    getAllExpenses(limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        category
        amount
        date
        description
        paymentMethod
        status
        submitterId
        department
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

// Query to get receipts for multiple expenses efficiently
const listAllReceiptsQuery = /* GraphQL */ `
  query ListReceipts($limit: Int, $nextToken: String) {
    listReceipts(limit: $limit, nextToken: $nextToken) {
      items {
        id
        expenseId
        fileName
        fileUrl
        fileKey
        fileType
        fileSize
        uploadedAt
      }
      nextToken
    }
  }
`;

const getExpenseQuery = /* GraphQL */ `
  query GetExpense($id: ID!) {
    getExpense(id: $id) {
      id
      title
      category
      amount
      date
      description
      paymentMethod
      status
      submitterId
      department
      createdAt
      updatedAt
    }
  }
`;

const expensesBySubmitterQuery = /* GraphQL */ `
  query ExpensesBySubmitter($submitterId: ID!, $limit: Int, $nextToken: String) {
    expensesBySubmitter(submitterId: $submitterId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        category
        amount
        date
        description
        paymentMethod
        status
        submitterId
        department
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const expensesByStatusQuery = /* GraphQL */ `
  query ExpensesByStatus($status: String!, $limit: Int, $nextToken: String) {
    expensesByStatus(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        category
        amount
        date
        description
        paymentMethod
        status
        submitterId
        department
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const listUsersQuery = /* GraphQL */ `
  query ListUsers($limit: Int, $nextToken: String) {
    listUsers(limit: $limit, nextToken: $nextToken) {
      items {
        id
        email
        name
        employeeId
        department
        managerEmail
        role
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// GraphQL Mutations
const createExpenseMutation = /* GraphQL */ `
  mutation CreateExpense($input: CreateExpenseInput!) {
    createExpense(input: $input) {
      id
      title
      category
      amount
      date
      description
      paymentMethod
      status
      submitterId
      department
      createdAt
      updatedAt
    }
  }
`;

const updateExpenseMutation = /* GraphQL */ `
  mutation UpdateExpense($input: UpdateExpenseInput!) {
    updateExpense(input: $input) {
      id
      title
      category
      amount
      date
      description
      paymentMethod
      status
      submitterId
      department
      createdAt
      updatedAt
    }
  }
`;

const deleteExpenseMutation = /* GraphQL */ `
  mutation DeleteExpense($id: ID!) {
    deleteExpense(id: $id) {
      id
    }
  }
`;

const createApprovalMutation = /* GraphQL */ `
  mutation CreateApproval($input: CreateApprovalInput!) {
    createApproval(input: $input) {
      id
      expenseId
      approverId
      status
      comments
      level
      timestamp
    }
  }
`;

const createReceiptMutation = /* GraphQL */ `
  mutation CreateReceipt($input: CreateReceiptInput!) {
    createReceipt(input: $input) {
      id
      expenseId
      fileName
      fileUrl
      fileKey
      fileType
      fileSize
      uploadedAt
    }
  }
`;

// Query for receipts by expense - uses index in production
const receiptsByExpenseQuery = /* GraphQL */ `
  query ReceiptsByExpense($expenseId: ID!) {
    receiptsByExpense(expenseId: $expenseId) {
      items {
        id
        expenseId
        fileName
        fileUrl
        fileKey
        fileType
        fileSize
        uploadedAt
      }
    }
  }
`;

// Fallback query using filter (for mock/development)
const listReceiptsByExpenseQuery = /* GraphQL */ `
  query ListReceipts($limit: Int, $nextToken: String) {
    listReceipts(limit: $limit, nextToken: $nextToken) {
      items {
        id
        expenseId
        fileName
        fileUrl
        fileKey
        fileType
        fileSize
        uploadedAt
      }
      nextToken
    }
  }
`;

/**
 * Record one candidate identity, plus the same address on every alias domain.
 *
 * The value is kept verbatim as well as expanded: GSI keys are case-sensitive, and
 * identityVariants() lowercases, so a row stored with capitals would otherwise become
 * unreachable the moment aliasing was introduced.
 */
const addIdentity = (found: Set<string>, value?: string | null): void => {
  const trimmed = (value || '').trim();
  if (!trimmed) return;

  found.add(trimmed);
  identityVariants(trimmed).forEach(variant => found.add(variant));
};

/**
 * Every string this user's expenses might be filed under, from data already to hand.
 *
 * `submitterId` is not written consistently. A single table holds rows keyed by
 * email and rows keyed by Cognito sub, and getTeamExpenses additionally matches on
 * the Cognito username (`azuread_…` for SSO users). Querying only some of those
 * shapes returns a clean empty list, which the UI renders identically to "you have
 * no expenses" — no error, nothing to notice.
 */
const resolveLocalIdentities = async (userId?: string, userEmail?: string): Promise<string[]> => {
  const found = new Set<string>();
  addIdentity(found, userId);
  addIdentity(found, userEmail);
  if (userEmail) addIdentity(found, userEmail.toLowerCase());

  try {
    const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');

    const current = await getCurrentUser();
    addIdentity(found, current?.userId);
    addIdentity(found, current?.username);

    // Federated usernames look like azuread_<providerId>_<email>. Rows written
    // during a native login would be filed under the bare email instead, so pull
    // the trailing address out as its own candidate.
    const username = current?.username || '';
    const at = username.indexOf('@');
    if (at > 0) {
      const underscore = username.lastIndexOf('_', at);
      if (underscore >= 0) {
        const embedded = username.slice(underscore + 1);
        addIdentity(found, embedded);
        addIdentity(found, embedded.toLowerCase());
      }
    }

    const claims: any = (await fetchAuthSession()).tokens?.idToken?.payload || {};
    addIdentity(found, claims.sub);
    addIdentity(found, claims.email);
    addIdentity(found, claims['cognito:username']);
    addIdentity(found, claims['custom:employeeId']);
  } catch (identityError: any) {
    console.warn(
      '[GraphQL] getMyExpenses: could not read the auth session for identity resolution:',
      identityError?.message || identityError
    );
  }

  return Array.from(found);
};

/**
 * Identities belonging to *other* Cognito accounts for the same person.
 *
 * Azure SSO creates an `azuread_…` account alongside any native one for the same
 * email, and auth.ts deliberately reports the federated sub as user.id while taking
 * role and name from the native account. An expense filed under one account's sub is
 * therefore invisible to the other.
 *
 * Costs an admin API round trip, so this only runs when the direct lookup found
 * nothing at all.
 */
const resolveSiblingIdentities = async (
  userEmail: string | undefined,
  alreadyTried: string[]
): Promise<string[]> => {
  const endpoint = process.env.REACT_APP_ADMIN_API_ENDPOINT;
  const email = (userEmail || '').trim().toLowerCase();
  if (!endpoint || !email) return [];

  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response = await fetch(`${endpoint}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    if (!response.ok) {
      console.warn('[GraphQL] getMyExpenses: sibling identity lookup returned', response.status);
      return [];
    }

    const body = await response.json();
    const tried = new Set(alreadyTried);
    const extra = new Set<string>();

    (body.users || []).forEach((candidate: any) => {
      const attrs: Record<string, string> = {};
      (candidate.Attributes || []).forEach((attr: any) => { attrs[attr.Name] = attr.Value; });

      // Compare canonicalised, not literal. The sibling account this lookup exists to
      // find is the one on the *other* domain, so an exact match could never see it —
      // which is why it reported "no sibling accounts" for a user who plainly had one.
      const candidateEmail = canonicalizeEmail(candidate.email || attrs.email);
      if (!candidateEmail || candidateEmail !== canonicalizeEmail(email)) return;

      [
        candidate.Username,
        candidate.username,
        attrs.sub,
        candidate.employeeId,
        attrs['custom:employeeId'],
      ].forEach(value => {
        const trimmed = (value || '').trim();
        if (trimmed && !tried.has(trimmed)) extra.add(trimmed);
      });
    });

    return Array.from(extra);
  } catch (siblingError: any) {
    console.warn(
      '[GraphQL] getMyExpenses: sibling identity lookup failed:',
      siblingError?.message || siblingError
    );
    return [];
  }
};

/**
 * Query the bySubmitter GSI once per identity, in parallel.
 *
 * Logs the row count for every identity including the zeroes — a miss has to be
 * visible in the console, otherwise "no expenses" and "wrong submitterId" look the
 * same from the outside.
 */
const queryExpensesBySubmitters = async (
  submitterIds: string[],
  limit: number,
  nextToken?: string
): Promise<{ rows: any[]; nextTokens: string[] }> => {
  const pages = await Promise.all(submitterIds.map(async submitterId => {
    try {
      const result: any = await getClient().graphql({
        query: expensesBySubmitterQuery,
        variables: { submitterId, limit, nextToken },
        ...getAuthMode(),
      } as any);
      return { submitterId, ...extractPage(result, 'expensesBySubmitter') };
    } catch (queryError: any) {
      const page = extractPage(queryError, 'expensesBySubmitter');
      console.warn(
        `[GraphQL] expensesBySubmitter(${submitterId}) failed: ${queryError?.message || queryError};` +
        ` recovered ${page.items.length} row(s)`
      );
      return { submitterId, ...page };
    }
  }));

  const rows: any[] = [];
  const nextTokens: string[] = [];
  pages.forEach(page => {
    const usable = liveRows(page.items, `expensesBySubmitter(${page.submitterId})`);
    console.log(`[GraphQL] expensesBySubmitter(${page.submitterId}) → ${usable.length} row(s)`);
    rows.push(...usable);
    if (page.nextToken) nextTokens.push(page.nextToken);
  });

  return { rows, nextTokens };
};

/**
 * GraphQL Expense Service for Amplify Mock/AppSync
 */
export class GraphQLExpenseService {
  
  /**
   * Get all expenses for a specific user.
   *
   * Looks the user up under every identity their rows might be filed under, because
   * submitterId is stored inconsistently across the table (email on some rows,
   * Cognito sub on others, and the azuread_ username for SSO sessions). If none of
   * the locally known identities match, falls back to the sibling Cognito accounts
   * registered against the same email before concluding there is nothing to show.
   */
  static async getMyExpenses(
    userId?: string,
    userEmail?: string,
    options: { limit?: number; nextToken?: string } = {}
  ): Promise<ExpenseConnection> {
    try {
      const startedAt = performance.now();
      console.log('[GraphQL] getMyExpenses called with userId:', userId, 'userEmail:', userEmail);

      const limit = options.limit || 25;
      const identities = await resolveLocalIdentities(userId, userEmail);
      console.log('[GraphQL] getMyExpenses resolved identities:', identities);

      let { rows, nextTokens } = await queryExpensesBySubmitters(identities, limit, options.nextToken);
      let queryCount = identities.length;

      // Nothing matched under any known identity. Before reporting an empty list,
      // check whether this person has another Cognito account (native vs Azure SSO)
      // whose id the rows were filed under.
      if (rows.length === 0) {
        const siblings = await resolveSiblingIdentities(userEmail, identities);
        if (siblings.length > 0) {
          console.warn(
            '[GraphQL] getMyExpenses: no rows under the signed-in identities; retrying with',
            siblings.length, 'sibling account identity(ies):', siblings
          );
          const retry = await queryExpensesBySubmitters(siblings, limit, options.nextToken);
          rows = retry.rows;
          nextTokens = retry.nextTokens;
          queryCount += siblings.length;
        } else {
          console.log('[GraphQL] getMyExpenses: no sibling Cognito accounts for this email to retry with');
        }
      }

      // Deduplicate by id; liveRows() already dropped anything AppSync nulled out.
      const seen = new Set<string>();
      const allItems = rows.filter(e => {
        if (!e || !e.id) return false;
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });

      console.log('[GraphQL] Total expenses found:', allItems.length);

      console.log('[Perf][MyExpenses] GraphQL getMyExpenses complete', {
        durationMs: Math.round(performance.now() - startedAt),
        queryCount,
        expenses: allItems.length,
        receiptLoading: 'deferred to ExpenseDetail',
      });

      return {
        items: allItems.map(GraphQLExpenseService.mapExpense),
        total: allItems.length,
        nextToken: nextTokens[0],
      };
    } catch (error: any) {
      console.error('[GraphQL] Error fetching my expenses:', error?.message || error);
      if (error?.errors) console.error('[GraphQL] Error details:', JSON.stringify(error.errors));
      return { items: [], total: 0 };
    }
  }

  /**
   * Get team expenses for a manager
   * Returns ONLY expenses from employees who report to this manager
   */
  static async getTeamExpenses(managerEmail: string): Promise<ExpenseConnection> {
    try {
      const t0 = performance.now();
      const profile: Record<string, number> = {};
      console.log('[PROFILE][getTeamExpenses] ═══════════════════════════════════════');
      console.log('[PROFILE][getTeamExpenses] START for manager:', managerEmail);
      
      // ═══ Step A: Manager Hierarchy Resolution ═══
      const tA = performance.now();
      let directReports: {
        email: string;
        username?: string;
        name?: string;
        sub?: string;
        employeeId?: string;
      }[] = [];
      let hierarchySource = 'none';
      
      try {
        const adminEndpoint = process.env.REACT_APP_ADMIN_API_ENDPOINT;
        if (adminEndpoint) {
          const tAuth = performance.now();
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          profile['A1_auth_session'] = Math.round(performance.now() - tAuth);
          console.log('[PROFILE][getTeamExpenses] Step A1 - Auth session:', profile['A1_auth_session'], 'ms');
          
          const tCognito = performance.now();
          const response = await fetch(`${adminEndpoint}/users`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            },
            mode: 'cors',
            credentials: 'omit'
          });
          profile['A2_cognito_api_call'] = Math.round(performance.now() - tCognito);
          console.log('[PROFILE][getTeamExpenses] Step A2 - Cognito Admin API call:', profile['A2_cognito_api_call'], 'ms, status:', response.status);
          
          if (response.ok) {
            const tParse = performance.now();
            const data = await response.json();
            const allUsers = data.users || [];
            directReports = allUsers
              .filter((u: any) => sameIdentity(u.managerEmail, managerEmail))
              .map((u: any) => {
                const attrs: Record<string, string> = {};
                (u.Attributes || []).forEach((attr: any) => { attrs[attr.Name] = attr.Value; });
                return {
                  email: u.email?.toLowerCase(),
                  // Kept in original case: submitterId is a DynamoDB key, so the
                  // GSI match is exact and case-sensitive.
                  username: u.username || u.Username,
                  name: u.name,
                  sub: attrs.sub,
                  employeeId: u.employeeId || attrs['custom:employeeId'],
                };
              });
            profile['A3_parse_and_filter'] = Math.round(performance.now() - tParse);
            hierarchySource = 'cognito-admin-api';
            console.log('[PROFILE][getTeamExpenses] Step A3 - Parse + filter:', profile['A3_parse_and_filter'], 'ms, totalUsers:', allUsers.length, ', directReports:', directReports.length);
          }
        }
      } catch (cognitoError) {
        console.warn('[PROFILE][getTeamExpenses] Cognito Admin API FAILED:', (cognitoError as any)?.message);
        try {
          const tGraphQLUsers = performance.now();
          const usersResult = await getClient().graphql({
            query: listUsersQuery,
            variables: { limit: 100 },
            ...getAuthMode()
          } as any);
          const usersData = getData(usersResult);
          const allUsers = usersData?.listUsers?.items || [];
          directReports = allUsers
            .filter((u: any) => sameIdentity(u.managerEmail, managerEmail))
            .map((u: any) => ({
              email: u.email?.toLowerCase(),
              // listUsers exposes no Attributes, so id is the only non-email handle.
              username: u.id,
              name: u.name,
              employeeId: u.employeeId,
            }));
          profile['A4_graphql_listUsers_fallback'] = Math.round(performance.now() - tGraphQLUsers);
          hierarchySource = 'graphql-listUsers';
          console.log('[PROFILE][getTeamExpenses] Step A4 - GraphQL listUsers fallback:', profile['A4_graphql_listUsers_fallback'], 'ms, directReports:', directReports.length);
        } catch (graphqlError) {
          console.warn('[PROFILE][getTeamExpenses] GraphQL listUsers also FAILED:', (graphqlError as any)?.message);
        }
      }

      profile['A_total_hierarchy'] = Math.round(performance.now() - tA);
      console.log('[PROFILE][getTeamExpenses] ───────────────────────────────────────');
      console.log('[PROFILE][getTeamExpenses] Step A TOTAL (Manager Hierarchy):', profile['A_total_hierarchy'], 'ms');
      console.log('[PROFILE][getTeamExpenses]   Source:', hierarchySource);
      console.log('[PROFILE][getTeamExpenses]   Direct reports:', directReports.length);
      console.log('[PROFILE][getTeamExpenses]   Emails:', directReports.map(r => r.email));

      if (directReports.length === 0) {
        console.log('[PROFILE][getTeamExpenses] No direct reports found — returning empty');
        return { items: [], total: 0 };
      }

      // ═══ Step B: Load Expenses (parallel queries per direct report) ═══
      const tB = performance.now();
      const expensePromises: Promise<any[]>[] = [];
      const queryIds: string[] = [];
      
      for (const report of directReports) {
        // Same shape problem as getMyExpenses: a report's rows may be filed under
        // their email, their Cognito username (azuread_… under SSO), or their sub.
        // Querying only two of the three silently drops that person's expenses.
        const submitterIds = Array.from(new Set([
          report.email,
          report.email?.toLowerCase(),
          report.username,
          report.sub,
          report.employeeId,
        ].filter(Boolean) as string[]));
        for (const submitterId of submitterIds) {
          queryIds.push(submitterId);
          expensePromises.push(
            (async () => {
              const tQuery = performance.now();
              try {
                const result = await getClient().graphql({
                  query: expensesBySubmitterQuery,
                  variables: { submitterId, limit: 100 },
                  ...getAuthMode()
                } as any);
                const data = getData(result);
                const items = data?.expensesBySubmitter?.items || [];
                console.log('[PROFILE][getTeamExpenses] Step B - expensesBySubmitter(' + submitterId + '):', Math.round(performance.now() - tQuery), 'ms, count:', items.length);
                return items;
              } catch {
                console.log('[PROFILE][getTeamExpenses] Step B - expensesBySubmitter(' + submitterId + ') FAILED:', Math.round(performance.now() - tQuery), 'ms');
                return [] as any[];
              }
            })()
          );
        }
      }

      console.log('[PROFILE][getTeamExpenses] Step B - Dispatching', expensePromises.length, 'parallel queries for submitterIds:', queryIds);
      const expenseArrays = await Promise.all(expensePromises);
      let allExpenses = expenseArrays.flat();
      profile['B_total_expense_queries'] = Math.round(performance.now() - tB);
      console.log('[PROFILE][getTeamExpenses] ───────────────────────────────────────');
      console.log('[PROFILE][getTeamExpenses] Step B TOTAL (Expense Queries):', profile['B_total_expense_queries'], 'ms');
      console.log('[PROFILE][getTeamExpenses]   Parallel queries:', expensePromises.length);
      console.log('[PROFILE][getTeamExpenses]   Raw expense count:', allExpenses.length);

      // Deduplicate by id, skipping rows AppSync returned as null
      const tDedup = performance.now();
      const seen = new Set<string>();
      allExpenses = liveRows(allExpenses, 'getTeamExpenses').filter(e => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });
      profile['B2_dedup'] = Math.round(performance.now() - tDedup);
      console.log('[PROFILE][getTeamExpenses]   After dedup:', allExpenses.length, '(dedup took', profile['B2_dedup'], 'ms)');
      console.log('[PROFILE][getTeamExpenses]   By status:', {
        SUBMITTED: allExpenses.filter((e: any) => e.status === 'SUBMITTED').length,
        MANAGER_APPROVED: allExpenses.filter((e: any) => e.status === 'MANAGER_APPROVED').length,
        REIMBURSED: allExpenses.filter((e: any) => e.status === 'REIMBURSED').length,
        DRAFT: allExpenses.filter((e: any) => e.status === 'DRAFT').length,
      });

      // ═══ Step C: Map expenses ═══
      const tC = performance.now();
      const mappedExpenses = allExpenses.map(GraphQLExpenseService.mapExpense);
      profile['C_map_expenses'] = Math.round(performance.now() - tC);
      console.log('[PROFILE][getTeamExpenses] ───────────────────────────────────────');
      console.log('[PROFILE][getTeamExpenses] Step C (Map Expenses):', profile['C_map_expenses'], 'ms');

      // ═══ FINAL SUMMARY ═══
      profile['TOTAL'] = Math.round(performance.now() - t0);
      console.log('[PROFILE][getTeamExpenses] ═══════════════════════════════════════');
      console.log('[PROFILE][getTeamExpenses] FINAL TIMING BREAKDOWN:');
      console.log('[PROFILE][getTeamExpenses]   Step A (Manager Hierarchy):  ', profile['A_total_hierarchy'], 'ms');
      console.log('[PROFILE][getTeamExpenses]     A1 Auth session:           ', profile['A1_auth_session'] || 0, 'ms');
      console.log('[PROFILE][getTeamExpenses]     A2 Cognito API call:       ', profile['A2_cognito_api_call'] || 0, 'ms');
      console.log('[PROFILE][getTeamExpenses]     A3 Parse & filter:         ', profile['A3_parse_and_filter'] || 0, 'ms');
      console.log('[PROFILE][getTeamExpenses]     A4 GraphQL fallback:       ', profile['A4_graphql_listUsers_fallback'] || 0, 'ms');
      console.log('[PROFILE][getTeamExpenses]   Step B (Expense Queries):    ', profile['B_total_expense_queries'], 'ms');
      console.log('[PROFILE][getTeamExpenses]     Parallel query count:      ', expensePromises.length);
      console.log('[PROFILE][getTeamExpenses]     Dedup:                     ', profile['B2_dedup'], 'ms');
      console.log('[PROFILE][getTeamExpenses]   Step C (Map Expenses):       ', profile['C_map_expenses'], 'ms');
      console.log('[PROFILE][getTeamExpenses]   ─────────────────────────────────');
      console.log('[PROFILE][getTeamExpenses]   TOTAL:                       ', profile['TOTAL'], 'ms');
      console.log('[PROFILE][getTeamExpenses]   Expense count:               ', mappedExpenses.length);
      console.log('[PROFILE][getTeamExpenses] ═══════════════════════════════════════');
      
      return { items: mappedExpenses, total: mappedExpenses.length };
    } catch (error: any) {
      console.error('[PROFILE][getTeamExpenses] FATAL ERROR:', error?.message || error);
      if (error?.errors) console.error('[PROFILE][getTeamExpenses] Error details:', JSON.stringify(error.errors));
      return { items: [], total: 0 };
    }
  }

  /**
   * Get all expenses (for admin)
   */
  static async getAllExpenses(userRole?: string): Promise<ExpenseConnection> {
    try {
      // Use expensesByStatus for each status to avoid full scan errors
      // This is more reliable than a full table scan
      const statuses = ['DRAFT', 'SUBMITTED', 'MANAGER_APPROVED', 'FINANCE_APPROVED', 'REIMBURSED', 'MANAGER_REJECTED', 'FINANCE_REJECTED'];
      let allItems: any[] = [];
      console.log('[GraphQL][FinanceDashboard] expense request start', {
        userRole,
        source: 'Expense',
        table: 'Expense',
        appliedFilters: {},
      });
      // First try the custom resolver. It is intended for Finance/Super Admin company-wide access.
      // It is absent from some deployed schemas, in which case AppSync rejects the
      // whole query on validation and we fall through to the scan below.
      allItems = await collectPages(
        getAllExpensesQuery,
        'getAllExpenses',
        { limit: 200 },
        'getAllExpenses resolver'
      );
      console.log('[GraphQL] getAllExpenses custom resolver returned:', allItems.length, 'expenses');

      // Then try the full scan (fast path) if the resolver did not return items.
      if (allItems.length === 0) {
        allItems = await collectPages(
          listExpensesQuery,
          'listExpenses',
          { limit: 200 },
          'getAllExpenses scan'
        );
        console.log('[GraphQL] getAllExpenses scan returned:', allItems.length, 'expenses');
      }

      // Always try per-status queries if no items were found through the resolver/scan.
      // The counts above deliberately exclude rows AppSync nulled out, so this
      // fallback still fires when a page came back as nothing but nulls.
      if (allItems.length === 0) {
        for (const status of statuses) {
          allItems.push(...await collectPages(
            expensesByStatusQuery,
            'expensesByStatus',
            { status, limit: 100 },
            `getAllExpenses byStatus(${status})`
          ));
        }
        console.log('[GraphQL] getAllExpenses per-status fallback returned:', allItems.length, 'expenses');
      }

      // Deduplicate
      const seen = new Set<string>();
      allItems = allItems.filter(e => {
        if (!e || !e.id) return false;
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });
      
      const expensesWithReceipts = await GraphQLExpenseService.attachReceiptsToExpenses(
        allItems.map(this.mapExpense)
      );

      console.log('[GraphQL][FinanceDashboard] expense request complete', {
        userRole,
        source: 'Expense',
        table: 'Expense',
        recordsScanned: allItems.length,
        recordsReturned: expensesWithReceipts.length,
        appliedFilters: {},
        totalAmount: expensesWithReceipts.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
      });
      
      return { items: expensesWithReceipts, total: allItems.length };
    } catch (error: any) {
      console.error('[GraphQL] Error fetching all expenses:', error?.message || error);
      return { items: [], total: 0 };
    }
  }

  /**
   * Get expenses pending finance approval
   */
  static async getPendingFinanceApproval(): Promise<ExpenseConnection> {
    try {
      // Use expensesByStatus GSI — the listExpenses query doesn't support filter params
      const items = await collectPages(
        expensesByStatusQuery,
        'expensesByStatus',
        { status: 'MANAGER_APPROVED', limit: 100 },
        'getPendingFinanceApproval'
      );

      const expensesWithReceipts = await GraphQLExpenseService.attachReceiptsToExpenses(
        items.map(GraphQLExpenseService.mapExpense)
      );

      return { items: expensesWithReceipts, total: items.length };
    } catch (error: any) {
      console.error('[GraphQL] Error fetching pending finance approvals:', error?.message || error);
      return { items: [], total: 0 };
    }
  }

  /**
   * Create a new expense draft
   */
  static async createDraft(input: CreateExpenseInput, userId: string, userName?: string, userDepartment?: string): Promise<Expense> {
    try {
      console.log('[GraphQL] createDraft called with userId:', userId, 'userName:', userName, 'userDepartment:', userDepartment);
      console.log('[GraphQL] createDraft input:', input);
      
      // Store user name in department field as "Department | Name" format
      // This allows us to display the submitter's name without a separate field
      // Use actual user department, fallback to 'Engineering' if not provided
      const actualDepartment = userDepartment || 'Engineering';
      const departmentWithName = userName ? `${actualDepartment} | ${userName}` : actualDepartment;
      
      console.log('[GraphQL] Using department:', departmentWithName);
      
      const result = await getClient().graphql({
        query: createExpenseMutation,
        variables: {
          input: {
            title: input.title,
            category: input.category,
            amount: input.amount,
            date: input.date,
            description: input.description || '',
            paymentMethod: input.paymentMethod,
            status: 'DRAFT',
            submitterId: userId,
            department: departmentWithName
          }
        },
        ...getAuthMode()
      } as any);
      
      // Check for GraphQL errors in the response
      const resultAny = result as any;
      if (resultAny.errors && resultAny.errors.length > 0) {
        console.error('[GraphQL] GraphQL errors:', resultAny.errors);
        throw new Error(resultAny.errors[0].message || 'GraphQL error');
      }
      
      const data = getData(result);
      console.log('[GraphQL] Created expense:', data?.createExpense);
      return GraphQLExpenseService.mapExpense(data?.createExpense);
    } catch (error: any) {
      console.error('[GraphQL] Error creating expense:', error);
      // Log more details if available
      if (error.errors) {
        console.error('[GraphQL] Error details:', error.errors);
      }
      throw error;
    }
  }

  /**
   * Update an expense
   */
  static async updateExpense(id: string, input: UpdateExpenseInput): Promise<Expense> {
    try {
      const updateInput: any = { id };
      if (input.title) updateInput.title = input.title;
      if (input.category) updateInput.category = input.category.toUpperCase().replace(/ /g, '_');
      if (input.amount) updateInput.amount = input.amount;
      if (input.date) updateInput.date = input.date;
      if (input.description !== undefined) updateInput.description = input.description;
      if (input.paymentMethod) updateInput.paymentMethod = input.paymentMethod.toUpperCase().replace(/ /g, '_');

      const result = await getClient().graphql({
        query: updateExpenseMutation,
        variables: { input: updateInput },
        ...getAuthMode()
      } as any);
      
      const data = getData(result);
      return this.mapExpense(data?.updateExpense);
    } catch (error) {
      console.error('[GraphQL] Error updating expense:', error);
      throw error;
    }
  }

  /**
   * Submit expense for approval.
   * Finance and Super Admin submissions can bypass manager approval; manager submissions stay submitted.
   */
  static async submitForApproval(id: string, submitterInfo?: { role?: string; managerEmail?: string }): Promise<Expense> {
    try {
      console.log('[GraphQL] submitForApproval called for expense:', id);
      console.log('[GraphQL] Submitter info:', JSON.stringify(submitterInfo));
      
      // Determine the appropriate status based on submitter.
      // Default to SUBMITTED so manager-created expenses still enter the approval queue.
      let newStatus = 'SUBMITTED';
      
      // Check if should bypass manager approval
      if (submitterInfo) {
        const role = (submitterInfo.role || '').toUpperCase();
        const managerEmail = submitterInfo.managerEmail || '';
        const hasManager = managerEmail.trim().length > 0;
        
        console.log('[GraphQL] Role check:', role);
        console.log('[GraphQL] Manager email:', managerEmail);
        console.log('[GraphQL] Has manager:', hasManager);
        
        // Bypass manager approval ONLY for Finance Admin or Super Admin.
        // Managers must remain SUBMITTED, even when managerEmail is empty.
        const isFinanceOrSuper = role === 'FINANCE_ADMIN' || role === 'SUPER_ADMIN';
        
        if (isFinanceOrSuper) {
          newStatus = 'MANAGER_APPROVED';
          console.log('[GraphQL] Bypassing manager approval - finance/super admin role:', role);
        } else {
          console.log('[GraphQL] Normal flow - expense needs manager approval (role:', role, ', hasManager:', hasManager, ')');
        }
      } else {
        console.log('[GraphQL] No submitter info provided - defaulting to SUBMITTED');
      }
      
      const result = await getClient().graphql({
        query: updateExpenseMutation,
        variables: {
          input: { id, status: newStatus }
        },
        ...getAuthMode()
      } as any);
      
      const data = getData(result);
      console.log('[GraphQL] Expense submitted for approval:', id, '-> Status:', newStatus);
      console.log('[GraphQL] Updated expense data:', data?.updateExpense);
      return this.mapExpense(data?.updateExpense);
    } catch (error) {
      console.error('[GraphQL] Error submitting expense:', error);
      throw error;
    }
  }

  /**
   * Delete a draft expense
   */
  static async deleteDraft(id: string): Promise<void> {
    try {
      await getClient().graphql({
        query: deleteExpenseMutation,
        variables: { id },
        ...getAuthMode()
      } as any);
      console.log('[GraphQL] Expense deleted:', id);
    } catch (error) {
      console.error('[GraphQL] Error deleting expense:', error);
      throw error;
    }
  }

  /**
   * Approve expense (manager or finance)
   */
  static async approveExpense(id: string, approverId: string, comments?: string): Promise<Expense> {
    try {
      console.log('[GraphQL] approveExpense called for:', id, 'by:', approverId);
      
      // Get current expense to determine next status
      const getResult = await getClient().graphql({
        query: getExpenseQuery,
        variables: { id },
        ...getAuthMode()
      } as any);
      
      const expenseData = getData(getResult);
      const expense = expenseData?.getExpense;
      console.log('[GraphQL] Current expense:', expense);
      
      if (!expense) throw new Error('Expense not found');

      let newStatus: string;
      let level: number;

      if (expense.status === 'SUBMITTED') {
        newStatus = 'MANAGER_APPROVED';
        level = 1;
      } else if (expense.status === 'MANAGER_APPROVED') {
        newStatus = 'FINANCE_APPROVED';
        level = 2;
      } else {
        throw new Error(`Expense cannot be approved in current status: ${expense.status}`);
      }

      console.log('[GraphQL] Will update status to:', newStatus);

      // Create approval record (optional - skip if resolver not set up)
      try {
        await getClient().graphql({
          query: createApprovalMutation,
          variables: {
            input: {
              expenseId: id,
              approverId,
              status: 'APPROVED',
              comments: comments || 'Approved',
              level
            }
          },
          ...getAuthMode()
        } as any);
        console.log('[GraphQL] Approval record created');
      } catch (approvalError) {
        console.warn('[GraphQL] Could not create approval record (resolver may not be set up):', approvalError);
        // Continue anyway - the important part is updating the expense status
      }

      // Update expense status
      console.log('[GraphQL] Updating expense status to:', newStatus);
      const result = await getClient().graphql({
        query: updateExpenseMutation,
        variables: {
          input: { id, status: newStatus }
        },
        ...getAuthMode()
      } as any);

      const data = getData(result);
      console.log('[GraphQL] Expense approved:', id, '-> Status:', newStatus);
      console.log('[GraphQL] Updated expense:', data?.updateExpense);
      return this.mapExpense(data?.updateExpense);
    } catch (error: any) {
      console.error('[GraphQL] Error approving expense:', error);
      if (error.errors) {
        console.error('[GraphQL] Error details:', error.errors);
      }
      throw error;
    }
  }

  /**
   * Reject expense
   */
  static async rejectExpense(id: string, approverId: string, comments: string): Promise<Expense> {
    try {
      const getResult = await getClient().graphql({
        query: getExpenseQuery,
        variables: { id },
        ...getAuthMode()
      } as any);
      
      const expenseData = getData(getResult);
      const expense = expenseData?.getExpense;
      if (!expense) throw new Error('Expense not found');

      let newStatus: string;
      let level: number;

      if (expense.status === 'SUBMITTED') {
        newStatus = 'MANAGER_REJECTED';
        level = 1;
      } else if (expense.status === 'MANAGER_APPROVED') {
        newStatus = 'FINANCE_REJECTED';
        level = 2;
      } else {
        throw new Error('Expense cannot be rejected in current status');
      }

      // Create rejection record
      try {
        await getClient().graphql({
          query: createApprovalMutation,
          variables: {
            input: {
              expenseId: id,
              approverId,
              status: 'REJECTED',
              comments,
              level
            }
          },
          ...getAuthMode()
        } as any);
        console.log('[GraphQL] Rejection record created');
      } catch (approvalError) {
        console.warn('[GraphQL] Could not create rejection record:', approvalError);
      }

      // Update expense status
      const result = await getClient().graphql({
        query: updateExpenseMutation,
        variables: {
          input: { id, status: newStatus }
        },
        ...getAuthMode()
      } as any);

      const data = getData(result);
      console.log('[GraphQL] Expense rejected:', id);
      return this.mapExpense(data?.updateExpense);
    } catch (error) {
      console.error('[GraphQL] Error rejecting expense:', error);
      throw error;
    }
  }

  /**
   * Mark expense as reimbursed
   */
  static async markReimbursed(id: string): Promise<Expense> {
    try {
      const result = await getClient().graphql({
        query: updateExpenseMutation,
        variables: {
          input: { id, status: 'REIMBURSED' }
        },
        ...getAuthMode()
      } as any);

      const data = getData(result);
      console.log('[GraphQL] Expense marked as reimbursed:', id);
      return this.mapExpense(data?.updateExpense);
    } catch (error) {
      console.error('[GraphQL] Error marking expense as reimbursed:', error);
      throw error;
    }
  }

  /**
   * Create a receipt record in the database
   * Falls back to localStorage if Receipt model is not deployed
   */
  static async createReceipt(receipt: {
    expenseId: string;
    fileName: string;
    fileUrl: string;
    fileKey?: string;
    fileType: string;
    fileSize: number;
  }): Promise<any> {
    try {
      console.log('[GraphQL] Creating receipt record:', receipt);
      
      // Extract S3 key from URL if not provided
      const fileKey = receipt.fileKey || GraphQLExpenseService.extractS3KeyFromUrl(receipt.fileUrl);
      
      const receiptData = {
        id: `receipt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        expenseId: receipt.expenseId,
        fileName: receipt.fileName,
        fileUrl: receipt.fileUrl,
        fileKey: fileKey,
        fileType: receipt.fileType,
        fileSize: receipt.fileSize,
        uploadedAt: new Date().toISOString()
      };
      
      try {
        const result = await getClient().graphql({
          query: createReceiptMutation,
          variables: {
            input: {
              expenseId: receipt.expenseId,
              fileName: receipt.fileName,
              fileUrl: receipt.fileUrl,
              fileKey: fileKey,
              fileType: receipt.fileType,
              fileSize: receipt.fileSize,
              uploadedAt: new Date().toISOString()
            }
          },
          ...getAuthMode()
        } as any);
        
        const data = getData(result);
        console.log('[GraphQL] Receipt record created in AppSync:', data?.createReceipt);
        return data?.createReceipt;
      } catch (graphqlError: any) {
        console.warn('[GraphQL] Could not save receipt to AppSync (model may not be deployed)');
        console.warn('[GraphQL] Falling back to localStorage');
        
        // Fallback: Store in localStorage
        const storageKey = `receipts_${receipt.expenseId}`;
        const existingReceipts = JSON.parse(localStorage.getItem(storageKey) || '[]');
        existingReceipts.push(receiptData);
        localStorage.setItem(storageKey, JSON.stringify(existingReceipts));
        
        console.log('[GraphQL] Receipt saved to localStorage:', receiptData.id);
        return receiptData;
      }
    } catch (error: any) {
      console.error('[GraphQL] Error creating receipt:', error);
      if (error.errors) {
        console.error('[GraphQL] Error details:', error.errors);
      }
      throw error;
    }
  }

  /**
   * Extract S3 key from a pre-signed URL
   * Returns key WITHOUT 'public/' prefix since Amplify adds it automatically
   */
  private static extractS3KeyFromUrl(url: string): string {
    try {
      // If URL contains 'public/', extract the path after it
      if (url.includes('public/')) {
        const match = url.match(/public\/(.+?)(\?|$)/);
        if (match) {
          return match[1]; // Return without 'public/' prefix
        }
      }
      
      // If URL contains 'receipts/', extract from there
      if (url.includes('receipts/')) {
        const match = url.match(/(receipts\/.+?)(\?|$)/);
        if (match) {
          return match[1];
        }
      }
      
      // Fallback: parse URL pathname
      const urlObj = new URL(url);
      let pathname = urlObj.pathname;
      // Remove leading slash
      if (pathname.startsWith('/')) {
        pathname = pathname.substring(1);
      }
      // Remove bucket name if present in path-style URL
      const parts = pathname.split('/');
      if (parts[0].includes('expense-receipts')) {
        pathname = parts.slice(1).join('/');
      }
      // Remove 'public/' if present
      if (pathname.startsWith('public/')) {
        pathname = pathname.substring(7);
      }
      return pathname;
    } catch {
      return url;
    }
  }

  /**
   * Get receipts for an expense
   * Tries multiple query approaches based on what's available in AppSync
   * Falls back to localStorage if Receipt model is not deployed
   */
  static async getReceiptsForExpense(expenseId: string): Promise<any[]> {
    try {
      console.log('[GraphQL] getReceiptsForExpense called for expenseId:', expenseId);
      
      // Try receiptsByExpense query first (production with GSI)
      try {
        const result = await getClient().graphql({
          query: receiptsByExpenseQuery,
          variables: { expenseId },
          ...getAuthMode()
        } as any);
        
        const data = getData(result);
        if (data?.receiptsByExpense?.items && data.receiptsByExpense.items.length > 0) {
          console.log('[GraphQL] Fetched receipts using receiptsByExpense:', data.receiptsByExpense.items.length);
          return data.receiptsByExpense.items;
        }
      } catch (prodError: any) {
        console.log('[GraphQL] receiptsByExpense query not available');
      }
      
      // Try listReceipts query (standard @model query)
      try {
        const result = await getClient().graphql({
          query: listReceiptsByExpenseQuery,
          variables: { limit: 1000 },
          ...getAuthMode()
        } as any);
        
        const data = getData(result);
        const allReceipts = data?.listReceipts?.items || [];
        
        if (allReceipts.length > 0) {
          // Filter by expenseId client-side
          const filteredReceipts = allReceipts.filter((r: any) => r.expenseId === expenseId);
          console.log('[GraphQL] Receipts for expense from AppSync:', filteredReceipts.length);
          return filteredReceipts;
        }
      } catch (listError: any) {
        console.log('[GraphQL] listReceipts query not available - checking localStorage');
      }
      
      // Fallback: Check localStorage
      const storageKey = `receipts_${expenseId}`;
      const localReceipts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (localReceipts.length > 0) {
        console.log('[GraphQL] Found receipts in localStorage:', localReceipts.length);
        return localReceipts;
      }
      
      console.log('[GraphQL] No receipts found for expense:', expenseId);
      return [];
    } catch (error: any) {
      console.error('[GraphQL] Error fetching receipts:', error);
      if (error.errors) {
        console.error('[GraphQL] Error details:', error.errors);
      }
      
      // Last resort: check localStorage
      const storageKey = `receipts_${expenseId}`;
      const localReceipts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return localReceipts;
    }
  }

  /**
   * Debug method to list all receipts in the database
   */
  static async listAllReceipts(): Promise<any[]> {
    try {
      const result = await getClient().graphql({
        query: listReceiptsByExpenseQuery,
        variables: { limit: 100 },
        ...getAuthMode()
      } as any);
      
      const data = getData(result);
      const receipts = data?.listReceipts?.items || [];
      console.log('[GraphQL] All receipts in database:', receipts.length);
      receipts.forEach((r: any) => {
        console.log('[GraphQL] Receipt:', r.id, 'expenseId:', r.expenseId, 'fileName:', r.fileName);
      });
      return receipts;
    } catch (error: any) {
      console.error('[GraphQL] Error listing all receipts:', error);
      return [];
    }
  }

  /**
   * Map GraphQL status to TypeScript enum value
   */
  private static mapStatus(graphqlStatus: string): ExpenseStatus {
    const statusMap: Record<string, ExpenseStatus> = {
      'DRAFT': ExpenseStatus.DRAFT,
      'SUBMITTED': ExpenseStatus.SUBMITTED,
      'MANAGER_APPROVED': ExpenseStatus.MANAGER_APPROVED,
      'MANAGER_REJECTED': ExpenseStatus.MANAGER_REJECTED,
      'FINANCE_APPROVED': ExpenseStatus.FINANCE_APPROVED,
      'FINANCE_REJECTED': ExpenseStatus.FINANCE_REJECTED,
      'REIMBURSED': ExpenseStatus.REIMBURSED,
      // Also handle if already in correct format
      'Draft': ExpenseStatus.DRAFT,
      'Submitted': ExpenseStatus.SUBMITTED,
      'Manager Approved': ExpenseStatus.MANAGER_APPROVED,
      'Manager Rejected': ExpenseStatus.MANAGER_REJECTED,
      'Finance Approved': ExpenseStatus.FINANCE_APPROVED,
      'Finance Rejected': ExpenseStatus.FINANCE_REJECTED,
      'Reimbursed': ExpenseStatus.REIMBURSED,
    };
    return statusMap[graphqlStatus] || ExpenseStatus.DRAFT;
  }

  /**
   * Helper method to fetch and attach receipts to a list of expenses
   * This improves performance by fetching all receipts in one query
   */
  private static async attachReceiptsToExpenses(expenses: Expense[]): Promise<Expense[]> {
    if (expenses.length === 0) {
      return expenses;
    }

    try {
      const startedAt = performance.now();
      let queryCount = 0;
      console.log('[GraphQL] Fetching receipts for', expenses.length, 'expenses');
      
      // Fetch all receipts in one query
      queryCount += 1;
      console.log('[Perf][MyExpenses] GraphQL query start', {
        name: 'ListReceipts',
        limit: 1000,
        queryCount,
      });
      const result = await getClient().graphql({
        query: listAllReceiptsQuery,
        variables: { limit: 1000 },
        ...getAuthMode()
      } as any);
      console.log('[Perf][MyExpenses] GraphQL query complete', {
        name: 'ListReceipts',
        durationMs: Math.round(performance.now() - startedAt),
      });
      
      const data = getData(result);
      const allReceipts = data?.listReceipts?.items || [];
      console.log('[GraphQL] Fetched', allReceipts.length, 'total receipts from DB');
      
      // Group receipts by expenseId
      const receiptsByExpenseId = new Map<string, any[]>();
      allReceipts.forEach((receipt: any) => {
        const expenseId = receipt.expenseId;
        if (!receiptsByExpenseId.has(expenseId)) {
          receiptsByExpenseId.set(expenseId, []);
        }
        receiptsByExpenseId.get(expenseId)!.push(receipt);
      });
      
      // S3 fallback: For expenses with no DB receipts, check S3 directly
      // Only do this for non-Draft expenses (they should have receipts)
      const expensesWithoutReceipts = expenses.filter(
        e => !receiptsByExpenseId.has(e.id) && e.status !== ExpenseStatus.DRAFT
      );
      
      if (expensesWithoutReceipts.length > 0 && expensesWithoutReceipts.length <= 20) {
        console.log('[GraphQL] Checking S3 for', expensesWithoutReceipts.length, 'expenses without DB receipts');
        for (const expense of expensesWithoutReceipts) {
          try {
            const s3StartedAt = performance.now();
            queryCount += 1;
            console.log('[Perf][MyExpenses] S3 receipt lookup start', {
              expenseId: expense.id,
              queryCount,
            });
            const s3Receipts = await StorageService.listReceiptsFromS3(expense.id);
            console.log('[Perf][MyExpenses] S3 receipt lookup complete', {
              expenseId: expense.id,
              durationMs: Math.round(performance.now() - s3StartedAt),
              receiptCount: s3Receipts.length,
            });
            if (s3Receipts.length > 0) {
              console.log('[GraphQL] Found', s3Receipts.length, 'S3 receipts for expense:', expense.id);
              receiptsByExpenseId.set(expense.id, s3Receipts);
              
              // Backfill DynamoDB records
              for (const r of s3Receipts) {
                try {
                  await GraphQLExpenseService.createReceipt({
                    expenseId: r.expenseId,
                    fileName: r.fileName,
                    fileUrl: r.fileUrl,
                    fileKey: r.fileKey,
                    fileType: r.fileType,
                    fileSize: r.fileSize
                  });
                } catch (backfillErr) {
                  // Silent - backfill is best-effort
                }
              }
            }
          } catch (s3Err) {
            // Silent - S3 fallback is best-effort
          }
        }
      }
      
      // Attach receipts to expenses
      console.log('[Perf][MyExpenses] attachReceiptsToExpenses complete', {
        durationMs: Math.round(performance.now() - startedAt),
        queryCount,
        expenses: expenses.length,
        receipts: allReceipts.length,
        expensesWithoutReceipts: expensesWithoutReceipts.length,
      });
      return expenses.map(expense => ({
        ...expense,
        receipts: receiptsByExpenseId.get(expense.id) || []
      }));
    } catch (error) {
      console.error('[GraphQL] Error fetching receipts:', error);
      // Return expenses without receipts if fetch fails
      return expenses;
    }
  }

  /**
   * Map GraphQL response to Expense type
   */
  private static mapExpense(data: any): Expense {
    if (!data) {
      throw new Error('No expense data');
    }
    
    return {
      id: data.id,
      title: data.title,
      category: data.category,
      amount: data.amount,
      date: data.date,
      description: data.description || '',
      // Rows written before paymentMethod was captured have none. Render the row
      // with the field blank rather than dropping the expense.
      paymentMethod: data.paymentMethod || ('' as Expense['paymentMethod']),
      status: GraphQLExpenseService.mapStatus(data.status),
      // Never undefined: consumers call string methods on this (e.g. the salary
      // summary matches it against Cognito accounts) and a bare undefined threw.
      submitterId: data.submitterId || '',
      department: data.department || '',
      receipts: [],
      approvals: [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  }

  /**
   * Get a single expense by ID
   */
  static async getExpense(id: string): Promise<Expense | null> {
    try {
      console.log('[GraphQL] getExpense called with id:', id);

      const result = await getClient().graphql({
        query: getExpenseQuery,
        variables: { id },
        ...getAuthMode()
      } as any);

      const data = getData(result);
      const expense = data?.getExpense;

      if (!expense) {
        console.log('[GraphQL] Expense not found:', id);
        return null;
      }

      console.log('[GraphQL] Found expense:', expense);
      return GraphQLExpenseService.mapExpense(expense);
    } catch (error: any) {
      console.error('[GraphQL] Error fetching expense:', error);
      if (error.errors) {
        console.error('[GraphQL] Error details:', error.errors);
      }
      throw error;
    }
  }

}
